import { MoveVertical } from "lucide-react";
import { TH, TD } from "../../../../components/ui";
import SectionShell from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber, CellText } from "../cells";

// The last band is open-ended. Infinity does not survive JSON, so it arrives as null.
const isOpenEnded = (b) => b.maxFt == null || b.maxFt === Infinity;

export default function AccessBandsSection({ config, readOnly, onSaved }) {
  const draft = useSectionDraft(config, "accessBands", onSaved, "Access bands");
  const bands = draft.value;
  if (!bands) return null;

  const maxMult = Math.max(1, ...bands.map((b) => Number(b.mult) || 0));
  const set = (i, key) => (v) => draft.update((d) => { d[i][key] = v; });

  return (
    <SectionShell
      title="Access Bands"
      subtitle="Working height decides the band; its multiplier scales field and cut-in hours."
      icon={MoveVertical}
      draft={draft}
      readOnly={readOnly}
    >
      <div className="overflow-x-auto rounded-[4px] border border-border">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}>Band</th>
              <th className={TH}>Label</th>
              <th className={`${TH} text-right`}>Up to height</th>
              <th className={`${TH} text-right`}>Hours multiplier</th>
              <th className={`${TH} w-[30%]`}>Effect</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((b, i) => {
              const mult = Number(b.mult) || 0;
              return (
                <tr key={b.id}>
                  <td className={`${TD} font-mono text-[11.5px]`}>{b.id}</td>
                  <td className={`${TD} min-w-[200px] py-1`}>
                    <CellText value={b.label} onChange={set(i, "label")} disabled={readOnly} ariaLabel={`${b.id} label`} />
                  </td>
                  <td className={`${TD} w-36 py-1`}>
                    {isOpenEnded(b) ? (
                      <span className="block text-right text-text_muted">No limit</span>
                    ) : (
                      <CellNumber value={b.maxFt} onChange={set(i, "maxFt")} disabled={readOnly} step="1" min={0} suffix="ft" ariaLabel={`${b.id} max height`} />
                    )}
                  </td>
                  <td className={`${TD} w-36 py-1`}>
                    <CellNumber value={b.mult} onChange={set(i, "mult")} disabled={readOnly} step="0.05" min={1} suffix="×" ariaLabel={`${b.id} multiplier`} />
                  </td>
                  <td className={TD}>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full border border-border_soft bg-surface_alt">
                        <div className="h-full bg-primary" style={{ width: `${(mult / maxMult) * 100}%` }} />
                      </div>
                      <span className="w-12 text-right text-[11.5px] tabular-nums text-text_secondary">+{Math.round((mult - 1) * 100)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11.5px] text-text_muted">
        Lift and boom bands also drive the Equipment schedule (scissor / boom lift weeks and deliveries).
      </p>
    </SectionShell>
  );
}
